import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatINR, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowUpRight, Calendar, Copy, Check, Gift, Share2, Wallet } from "lucide-react";
import { toast } from "sonner";
import ReferralPoster from "../components/ReferralPoster";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      weekday: "short", day: "numeric", month: "short",
      hour: "numeric", minute: "2-digit", hour12: true,
    });
  } catch { return iso; }
}

export default function Dashboard() {
  const { user, updateProfile } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showPoster, setShowPoster] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);
  const [payingSeq, setPayingSeq] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/enrollments/me").then((r) => setEnrollments(r.data)).catch(() => {}),
      api.get("/sessions/upcoming").then((r) => setSessions(r.data)).catch(() => {}),
      api.get("/payments/plans/mine").then((r) => setPlans(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const payInstalment = async (planId, seq) => {
    setPayingSeq(`${planId}-${seq}`);
    try {
      const { data } = await api.post(`/payments/plans/${planId}/pay/${seq}`);
      const formEl = document.createElement("form");
      formEl.method = "POST";
      formEl.action = data.action;
      Object.entries(data.fields).forEach(([k, v]) => {
        const input = document.createElement("input");
        input.type = "hidden"; input.name = k; input.value = String(v);
        formEl.appendChild(input);
      });
      document.body.appendChild(formEl);
      formEl.submit();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Payment failed");
      setPayingSeq(null);
    }
  };

  const referralCode = user?.referral_code || "—";
  const referralUrl = typeof window !== "undefined"
    ? `${window.location.origin}/register?ref=${referralCode}`
    : `/register?ref=${referralCode}`;

  const copyReferral = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Couldn't copy — select and copy manually");
    }
  };

  return (
    <div data-testid="dashboard-page" className="bg-novya-dark min-h-[calc(100vh-4rem)] text-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">
          Student console
        </div>
        <h1 className="mt-4 font-display font-black text-5xl md:text-7xl tracking-tighter leading-[0.95]">
          Hello, {user?.name?.split(" ")[0] || "learner"}<span className="text-novya-accent">.</span>
        </h1>
        <p className="mt-6 text-white/60 max-w-xl">
          Your enrolled programs, upcoming live sessions, and referral wallet — all in one place.
        </p>

        {/* Phone / WhatsApp reminder prompt */}
        {!user?.phone && enrollments.length > 0 && (
          <div className="mt-10 border border-novya-accent/40 bg-novya-accent/5 p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6" data-testid="phone-banner">
            <div className="flex-1">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-novya-accent">Never miss a live session</div>
              <h3 className="mt-2 font-display font-bold text-xl md:text-2xl">Add your WhatsApp number for 24-hour session reminders.</h3>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="tel"
                value={phoneDraft}
                onChange={(e) => setPhoneDraft(e.target.value)}
                placeholder="+91…"
                data-testid="phone-input"
                className="px-3 py-2.5 bg-transparent border border-white/20 text-white outline-none focus:border-novya-accent w-48"
              />
              <button
                onClick={async () => {
                  if (!phoneDraft.trim()) return;
                  setSavingPhone(true);
                  const res = await updateProfile({ phone: phoneDraft.trim() });
                  setSavingPhone(false);
                  if (res.ok) { toast.success("Phone saved — reminders on"); setPhoneDraft(""); }
                  else toast.error(res.error);
                }}
                disabled={savingPhone || !phoneDraft.trim()}
                data-testid="save-phone-btn"
                className="px-5 py-2.5 bg-novya-accent text-white font-medium text-sm disabled:opacity-50"
              >
                {savingPhone ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}

        {/* Enrollments */}
        <div className="mt-16">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="font-display font-bold text-2xl tracking-tight">Your programs</h2>
            <Link to="/programs" data-testid="dashboard-browse-btn" className="font-mono text-xs tracking-[0.2em] uppercase text-white/60 link-underline">
              Browse all →
            </Link>
          </div>

          {loading ? (
            <div className="font-mono text-xs tracking-[0.3em] uppercase text-white/40">Loading…</div>
          ) : enrollments.length === 0 ? (
            <div data-testid="dashboard-empty" className="border border-white/10 p-10 md:p-16">
              <div className="font-display font-black text-3xl md:text-4xl tracking-tighter leading-tight max-w-xl">
                No programs yet. Pick a track and start the leap.
              </div>
              <Link
                to="/programs"
                data-testid="dashboard-pick-track-btn"
                className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-white text-novya-ink font-medium hover:bg-novya-accent hover:text-white transition-colors"
              >
                Pick a track <ArrowUpRight size={16} />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {enrollments.map((e) => (
                <Link
                  key={e._id}
                  to={`/programs/${e.program_slug}`}
                  data-testid={`enrollment-${e.program_slug}`}
                  className="bg-novya-dark p-8 flex flex-col gap-6 hover:bg-novya-dark-surface transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-novya-accent">
                      {e.status || "active"}
                    </div>
                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40">
                      {e.program?.duration}
                    </div>
                  </div>
                  <h3 className="font-display font-black text-3xl tracking-tighter leading-tight">
                    {e.program_title}
                  </h3>
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 mb-2">
                      Progress · {e.progress ?? 0}%
                    </div>
                    <div className="h-1 bg-white/10 relative">
                      <div className="absolute h-1 bg-novya-accent" style={{ width: `${e.progress ?? 0}%` }} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Payment plans / instalments */}
        {plans.length > 0 && (
          <div className="mt-20" data-testid="plans-section">
            <div className="flex items-baseline justify-between mb-6">
              <h2 className="font-display font-bold text-2xl tracking-tight">Payment plan</h2>
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40">Instalments</span>
            </div>
            <div className="border border-white/10 divide-y divide-white/10">
              {plans.map((p) => (
                <div key={p._id} className="p-6" data-testid={`plan-${p._id}`}>
                  <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-5">
                    <div>
                      <div className="font-display font-bold text-xl">{p.program_title}</div>
                      <div className="mt-1 font-mono text-[10px] tracking-[0.2em] uppercase text-white/40">
                        {p.status} · Total {formatINR(p.total_amount)}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {p.instalments.map((i) => {
                      const paid = i.status === "paid";
                      const isPaying = payingSeq === `${p._id}-${i.seq}`;
                      return (
                        <div
                          key={i.seq}
                          data-testid={`plan-inst-${p._id}-${i.seq}`}
                          className={`border p-4 ${paid ? "border-novya-accent/40 bg-novya-accent/5" : "border-white/10"}`}
                        >
                          <div className="flex justify-between items-baseline">
                            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">
                              Instalment {i.seq}/3
                            </div>
                            <div className={`font-mono text-[10px] tracking-[0.2em] uppercase ${paid ? "text-novya-accent" : "text-white/40"}`}>
                              {i.status}
                            </div>
                          </div>
                          <div className="mt-2 font-display font-bold text-lg">{formatINR(i.amount)}</div>
                          <div className="font-mono text-[10px] text-white/50 mt-1">
                            Due {new Date(i.due_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                          </div>
                          {!paid && (
                            <button
                              onClick={() => payInstalment(p._id, i.seq)}
                              disabled={isPaying}
                              data-testid={`pay-inst-${p._id}-${i.seq}`}
                              className="mt-3 w-full py-2 bg-novya-accent text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                            >
                              {isPaying ? "Redirecting…" : "Pay now"}
                            </button>
                          )}
                          {paid && (
                            <div className="mt-3 flex items-center gap-1 text-xs text-novya-accent">
                              <Check size={14} /> Paid
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cohort Calendar */}
        <div className="mt-20">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="font-display font-bold text-2xl tracking-tight">Upcoming sessions</h2>
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40">Cohort calendar</span>
          </div>
          {sessions.length === 0 ? (
            <div data-testid="sessions-empty" className="border border-white/10 p-10 flex items-start gap-6">
              <Calendar size={28} className="text-novya-accent shrink-0 mt-1" />
              <div>
                <div className="font-display font-bold text-xl">No sessions scheduled yet.</div>
                <div className="mt-2 text-sm text-white/60 max-w-lg leading-relaxed">
                  Once your cohort kicks off, weekly live sessions with mentors will appear here — with the meeting link one click away.
                </div>
              </div>
            </div>
          ) : (
            <div className="border border-white/10 divide-y divide-white/10" data-testid="sessions-list">
              {sessions.map((s) => (
                <a
                  key={s._id}
                  href={s.meeting_url || "#"}
                  target="_blank"
                  rel="noreferrer"
                  className="p-6 grid grid-cols-12 gap-4 hover:bg-novya-dark-surface transition-colors"
                >
                  <div className="col-span-3 font-mono text-xs text-white/60">{formatDate(s.starts_at)}</div>
                  <div className="col-span-6">
                    <div className="font-display font-bold text-lg">{s.title}</div>
                    <div className="text-sm text-white/50">{s.description}</div>
                  </div>
                  <div className="col-span-3 text-right font-mono text-xs text-novya-accent">Join →</div>
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Referral */}
        <div className="mt-20 border border-white/10 p-8 md:p-12" data-testid="referral-card">
          <div className="flex items-start gap-4">
            <Gift size={28} className="text-novya-accent shrink-0 mt-1" />
            <div className="flex-1">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">Referral wallet</div>
              <h3 className="mt-2 font-display font-black text-3xl tracking-tighter">
                Share Novya. Earn credit.
              </h3>
              <p className="mt-3 text-sm text-white/60 max-w-2xl leading-relaxed">
                Every friend who enrolls with your code saves ₹2,000. You earn ₹1,000 in credit — automatically deducted from your next enrollment.
              </p>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="border border-white/10 p-5">
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">Your code</div>
                  <div className="mt-2 font-mono font-bold text-xl" data-testid="referral-code-display">{referralCode}</div>
                </div>
                <div className="border border-white/10 p-5">
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">Credit balance</div>
                  <div className="mt-2 font-display font-bold text-xl text-novya-accent" data-testid="referral-credit-display">
                    {formatINR(user?.referral_credit_inr || 0)}
                  </div>
                </div>
                <button
                  onClick={copyReferral}
                  data-testid="copy-referral-btn"
                  className="border border-white/10 p-5 text-left hover:bg-novya-dark-surface transition-colors"
                >
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">Share link</div>
                  <div className="mt-2 flex items-center gap-2 font-mono text-sm">
                    {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy link</>}
                  </div>
                </button>
                <button
                  onClick={() => setShowPoster(true)}
                  data-testid="show-poster-btn"
                  className="border border-white/10 p-5 text-left hover:bg-novya-dark-surface transition-colors"
                >
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">UPI-ready QR</div>
                  <div className="mt-2 flex items-center gap-2 font-mono text-sm">
                    <Share2 size={14} /> Get poster
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showPoster && (
        <ReferralPoster code={referralCode} onClose={() => setShowPoster(false)} />
      )}
    </div>
  );
}
